import { audioManager } from '../audio/AudioManager.js';
import { iconText } from './iconText.js';

// The dashboard you see while driving: the speed, the speed LIMIT that applies where you
// are, an „R" while you're backing up, the tank/battery and the odometer. Camera-pinned
// (scrollFactor 0, depth 50) down the LEFT EDGE, below the friends button (20, 20) and the
// outfit badge (~55, 90) and clear of the money bar in the top-right. Not the bottom-left,
// where it once lived: on a phone the touch joystick sits there and hid the whole
// dashboard. Created hidden — the street builds one per scene and the car controller shows
// it on entry and hides it on park.
//
// No rev counter and no gear readout any more (v2): a child learning to drive reads the
// speed and the sign, and neither number ever meant anything to them. What replaced them is
// the thing an instructor actually shouts about — "you're over the limit".
//
//   const hud = new CarHud(scene);
//   hud.show();
//   hud.setLimit(50);                // round sign beside the speed; null takes it off
//   hud.update({ speed: 62, fuelPct: 48, ev: false, km: 12.4 });   // 62 > 50+3 → red + pulse
//   hud.setReversing(true);          // „R"
//   hud.setPassengers(1);            // 👥 1 beside the speed; 0 hides it again
//   hud.setFuelVisible(false);       // no tank at all (the driving school's car)
//   hud.beep();                      // the reversing bleep (WebAudio; obeys the sfx mute)
//   hud.hide();

const PAD = 16;                 // margin from the left edge
const TOP = 150;                // clears the friends button + the outfit badge above it
const W = 250, H = 148;         // panel box
const BAR_W = 200, BAR_H = 10;  // fuel bar

const COLOR_SPEED = '#ffd166';
const COLOR_SPEED_OVER = '#ff5252';
const COLOR_FUEL_OK = 0x66cc66;
const COLOR_FUEL_LOW = 0xffb84d;
const COLOR_FUEL_EMPTY = 0xff5252;

// The speed sign: a white disc with a red ring, exactly like the real one.
const SIGN_R = 17;
const SIGN_RING = 4;
const SIGN_X = 176, SIGN_Y = 26;      // centre, relative to the panel's top-left
const OVER_BY = 3;                    // km/h of slack before the speed goes red

// The reversing bleep, when no sfx clip fits: a short square-wave blip.
const BEEP_HZ = 1000;
const BEEP_MS = 80;
const BEEP_GAIN = 0.06;

export class CarHud {
  constructor(scene) {
    this.scene = scene;
    const x = PAD;
    const y = TOP;

    const pin = (o) => o.setScrollFactor(0).setDepth(50).setVisible(false);

    this.bg = pin(scene.add.rectangle(x, y, W, H, 0x000000, 0.5)
      .setOrigin(0, 0).setStrokeStyle(2, 0xffd166));

    this.speedText = pin(iconText(scene, x + 12, y + 10, '', {
      fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '26px', color: COLOR_SPEED,
      fontStyle: 'bold',
    }).setOrigin(0, 0));

    // „R" — the only gear that ever needs saying out loud, and only while it's engaged.
    this.revText = pin(iconText(scene, x + 12, y + 46, 'R', {
      fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '22px', color: '#ff5252',
      fontStyle: 'bold',
    }).setOrigin(0, 0));

    this.fuelText = pin(iconText(scene, x + 12, y + 72, '', {
      fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '18px', color: '#ffffff',
    }).setOrigin(0, 0));
    this.fuelBg = pin(scene.add.rectangle(x + 12, y + 100, BAR_W, BAR_H, 0x222222, 0.9).setOrigin(0, 0));
    this.fuelFill = pin(scene.add.rectangle(x + 12, y + 100, 0, BAR_H, COLOR_FUEL_OK).setOrigin(0, 0));

    this.odoText = pin(iconText(scene, x + 12, y + 118, '', {
      fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '16px', color: '#cccccc',
    }).setOrigin(0, 0));

    // Who's aboard, from the server's `car_passengers` — on the speed line's right, so a
    // driver can see at a glance that someone is still in the back before parking.
    this.paxText = pin(iconText(scene, x + W - 12, y + 14, '', {
      fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '18px', color: '#ffffff',
    }).setOrigin(1, 0));

    // The limit sign lives in a Container so the "you're speeding" pulse is one tween on
    // one scale, instead of three objects kept in step by hand.
    this.limitText = iconText(scene, 0, 1, '', {
      fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '17px', color: '#111111',
      fontStyle: 'bold',
    }).setOrigin(0.5, 0.5);
    this.limitBox = scene.add.container(x + SIGN_X, y + SIGN_Y, [
      scene.add.circle(0, 0, SIGN_R, 0xffffff).setStrokeStyle(SIGN_RING, 0xd32f2f),
      this.limitText,
    ]).setScrollFactor(0).setDepth(50).setVisible(false);

    this._passengers = 0;
    this._fuelHidden = false;
    this._reversing = false;
    this._limit = null;
    this._speed = 0;
    this._pulsing = false;
    this._pulseTween = null;
    this._actx = null;

    this._parts = [this.bg, this.speedText, this.fuelText, this.fuelBg, this.fuelFill,
      this.odoText];

    scene.events.once('shutdown', () => this.destroy());
  }

  setVisible(on) {
    for (const p of this._parts) p.setVisible(on);
    this._shown = on;
    this.paxText.setVisible(on && this._passengers > 0);   // never an empty badge
    this.revText.setVisible(on && this._reversing);        // …nor an R in a forward gear
    this.limitBox.setVisible(on && this._limit != null);   // …nor a blank sign
    if (!on) this._setPulse(false);
    this.setFuelVisible(!this._fuelHidden);                // …nor a gauge we're hiding
  }

  /**
   * Show or hide the tank readout. The driving school's car has a tank the player never
   * touches (it never drains, never refuels), so a gauge pinned at 100% is just noise —
   * the dashboard drops it entirely for the lesson.
   */
  setFuelVisible(on) {
    this._fuelHidden = !on;
    const vis = !!this._shown && !!on;
    this.fuelText.setVisible(vis);
    this.fuelBg.setVisible(vis);
    this.fuelFill.setVisible(vis);
  }

  /** How many people are riding along. 0 takes the badge off the dashboard. */
  setPassengers(n) {
    this._passengers = Math.max(0, n | 0);
    this.paxText.setText(this._passengers > 0 ? `{friends} ${this._passengers}` : '');
    this.paxText.setVisible(!!this._shown && this._passengers > 0);
  }

  /**
   * The speed limit in force where the car is, in km/h — the street's own 50, or whatever
   * the route's current segment says. `null` takes the sign off and stops the speed ever
   * going red (a road with no sign cannot be sped on).
   */
  setLimit(kmh) {
    this._limit = Number.isFinite(kmh) && kmh > 0 ? Math.round(kmh) : null;
    this.limitText.setText(this._limit == null ? '' : String(this._limit));
    this.limitBox.setVisible(!!this._shown && this._limit != null);
    this._applySpeed();
  }

  /** Backing up: „R" on the dashboard. */
  setReversing(on) {
    this._reversing = !!on;
    this.revText.setVisible(!!this._shown && this._reversing);
  }

  show() { this.setVisible(true); }
  hide() { this.setVisible(false); }

  update({ speed = 0, fuelPct = 0, ev = false, km = 0 } = {}) {
    this._speed = speed;
    this._applySpeed();

    const pct = Math.max(0, Math.min(100, fuelPct));
    this.fuelText.setText(`${ev ? '{battery}' : '{fuel}'} ${Math.round(pct)}%`);
    this.fuelFill.width = BAR_W * (pct / 100);
    this.fuelFill.setFillStyle(pct <= 0 ? COLOR_FUEL_EMPTY : pct < 15 ? COLOR_FUEL_LOW : COLOR_FUEL_OK);

    this.odoText.setText(`${km.toFixed(1)} km`);
  }

  // Speeding is exactly one thing on this dashboard: the number goes red and the sign
  // beats. Both come off the same test, so they can never disagree.
  _applySpeed() {
    const over = this._limit != null && this._speed > this._limit + OVER_BY;
    this.speedText.setText(`${Math.round(this._speed)} km/h`);
    this.speedText.setColor(over ? COLOR_SPEED_OVER : COLOR_SPEED);
    this._setPulse(over && !!this._shown && this._limit != null);
  }

  _setPulse(on) {
    if (on === this._pulsing) return;
    this._pulsing = on;
    this._pulseTween?.remove();
    this._pulseTween = null;
    this.limitBox.setScale(1);
    if (!on) return;
    this._pulseTween = this.scene.tweens.add({
      targets: this.limitBox, scale: 1.18, duration: 300, yoyo: true, repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * The reversing bleep. There is no „beep" clip in the sfx set (see audio/eventMap.js), so
   * this is a 1 kHz, 80 ms square-wave blip straight out of WebAudio — silent when the
   * player has muted the sound effects, and silent (rather than throwing) anywhere WebAudio
   * is missing or still locked.
   */
  beep() {
    if (audioManager.sfxMuted) return;
    try {
      const Ctx = window.AudioContext ?? window.webkitAudioContext;
      if (!Ctx) return;
      this._actx ??= new Ctx();
      if (this._actx.state === 'suspended') this._actx.resume?.();
      const osc = this._actx.createOscillator();
      const gain = this._actx.createGain();
      osc.type = 'square';
      osc.frequency.value = BEEP_HZ;
      gain.gain.value = BEEP_GAIN;
      osc.connect(gain);
      gain.connect(this._actx.destination);
      const t = this._actx.currentTime;
      osc.start(t);
      osc.stop(t + BEEP_MS / 1000);
    } catch { /* no audio here — a silent reverse is fine */ }
  }

  destroy() {
    this._setPulse(false);
    for (const p of this._parts) p.destroy();
    this.paxText.destroy();
    this.revText.destroy();
    this.limitBox.destroy();   // …and the circle + number inside it
    try { this._actx?.close?.(); } catch { /* already gone */ }
    this._actx = null;
    this._parts = [];
  }
}
